import {Brick, registerBrick, BrickContext, Predicate, Transaction, ErrorFlow, File as OFile} from 'olympe';
import Assessment from "../../../../models/Assessment";
import Unit from "../../../../models/Unit";
import Observation from "../../../../models/Observation";
import Report from "../../../../models/Report";
import {
    buildComponentChartDatasets,
    buildSystemChartDatasets,
    formPinsArray,
    getChartHeight, getComponentDefectChartRawData,
    getSystemDefectChartRawData
} from "../function/defectChartData";
import SystemDefectChart from "../../components/SystemDefectChart";
import ComponentDefectChart from "../../components/ComponentDefectChart";
import System from "../../../../models/System";
import {dataUrlToBinary} from "@olympeio/core";
import Component from "../../../../models/Component";

export default class GenerateChartImages extends Brick {

    /**
     * @override
     * @protected
     * @param {!BrickContext} $
     * @param {Report} report
     * @param {string} config
     * @param {function()} forwardEvent
     * @param {function(*)} setErrorFlow
     */
    async update($, [report, config], [forwardEvent, setErrorFlow]) {
        // 1. Get all units of the assessment
        const units = report.follow(Assessment.reportsRel.getInverse())
            .follow(Assessment.unitsRel)
            .executeFromCache();

        // 2. Retrieve existing charts for observations to keep only one picture:
        const resultCharts = units.map((unit) => unit.follow(Unit.observationsRel).andReturn()
            .follow(Report.observationsRel.getInverse())
            .filter(Predicate.in(report))
            .back()
            .follow(Observation.chartImageResultsRel)
            .execute());
        const overallCharts = units.map((unit) => unit.follow(Unit.observationsRel).andReturn()
            .follow(Report.observationsRel.getInverse())
            .filter(Predicate.in(report))
            .back()
            .follow(Observation.charImageOverallRel)
            .execute());

        // Execute the required queries and keep them in cache
        $.set('prefetchedData', await Promise.all([...resultCharts, ...overallCharts]));

        const transaction = new Transaction(true);

        // Add the System's charts
        if (config['include_main_charts']) {
            for (const unit of units.values()) {
                unit.follow(Unit.systemsRel).andReturn()
                    .follow(Observation.targetRel.getInverse()).andReturn()
                    .follow(Report.observationsRel.getInverse())
                    .filter(Predicate.in(report))
                    .back()
                    .follow(Unit.observationsRel.getInverse())
                    .filter(Predicate.in(unit))
                    .executeFromCache()
                    .forEach(([system, observation]) => {
                        this.generateSystemChart(transaction, report, unit, system, observation);
                    });
            }
        }

        if (config['components_chart_results'] || config['components_chart_overall']) {
            for (const unit of units.values()) {
                unit.follow(Unit.systemsRel)
                    .follow(System.allComponentsRel).andReturn()
                    .follow(Component.inspectionsRel).back() // Only keep component having direct inspections.
                    .follow(Observation.targetRel.getInverse()).andReturn()
                    .follow(Report.observationsRel.getInverse())
                    .filter(Predicate.in(report))
                    .back()
                    .follow(Unit.observationsRel.getInverse())
                    .filter(Predicate.in(unit))
                    .executeFromCache()
                    .forEach(([component, observation]) => {
                        if (config['components_chart_overall']) {
                            this.generateComponentChart(transaction, report, unit, component, observation, true);
                        }

                        if (config['components_chart_results']) {
                            this.generateComponentChart(transaction, report, unit, component, observation, false);
                        }
                    });
            }
        }

        $.remove('prefetchedData');

        transaction.executeAsLarge().then(forwardEvent).catch((e) => {
            setErrorFlow(ErrorFlow.create(`Failed to generate chart images for the report: ${e}`, 0));
        });
    }

    /**
     * @private
     * @param {Transaction} transaction
     * @param {Report} report
     * @param {Unit} unit
     * @param {System} system
     * @param {Observation} observation
     */
    generateSystemChart(transaction, report, unit, system, observation) {
        const [componentsWithInspections, aspects, resultMapping] = getSystemDefectChartRawData(system, unit, report)

        const chartData = buildSystemChartDatasets('Overall components condition', componentsWithInspections, aspects, resultMapping);
        if (chartData.labels.length === 0) {
            return; // Do not generate the system chart image if there are no data.
        }

        const height = getChartHeight(chartData);
        const width = Math.max(640, Math.floor(height * 2 / 3)); // Keep the ratio so that it appears properly on a printed document
        const pinsArray = componentsWithInspections.reduce((array, item) => {
            array.push(formPinsArray(item, aspects, resultMapping));
            return array;
        }, []);
        const canvas = document.createElement('canvas');
        canvas.setAttribute('width', `${width}px`);
        canvas.setAttribute('height', `${height}px`);
        const chart = SystemDefectChart.createChart(canvas, chartData, pinsArray, true);
        const dataUrl = chart.toBase64Image();

        let imageTag = observation.follow(Observation.charImageOverallRel).executeFromCache().getFirst()?.getTag();
        if (!imageTag) {
            imageTag = transaction.create(OFile);
            transaction.createRelation(Observation.charImageOverallRel, observation, imageTag);
        }
        OFile.setContent(transaction, imageTag, `systemDefectChart_${system.getName()}`, dataUrlToBinary(dataUrl), 'image/png');
    }

    /**
     * @private
     * @param {Transaction} transaction
     * @param {Report} report
     * @param {Unit} unit
     * @param {Component} component
     * @param {Observation} observation
     * @param {boolean} isOverall
     */
    generateComponentChart(transaction, report, unit, component, observation, isOverall) {
        const [inspections, aspects, resultMapping] = getComponentDefectChartRawData(component, unit, report);

        const graphResult = isOverall ? 'Inspections results overall aspects' : 'Inspection results';
        const chartData = buildComponentChartDatasets(graphResult, inspections, aspects, resultMapping);
        if (chartData.labels.length === 0) {
            return; // Do not generate the component chart image if there are no data.
        }

        const height = getChartHeight(chartData);
        const width = Math.max(640, Math.floor(height * 2 / 3)); // Keep the ratio so that it appears properly on a printed document
        const canvas = document.createElement('canvas');
        canvas.setAttribute('width', `${width}px`);
        canvas.setAttribute('height', `${height}px`);
        const chart = ComponentDefectChart.createChart(canvas, chartData, true);
        const dataUrl = chart.toBase64Image();

        const relation = isOverall ? Observation.charImageOverallRel : Observation.chartImageResultsRel;
        let imageTag = observation.follow(relation).executeFromCache().getFirst()?.getTag();
        if (!imageTag) {
            imageTag = transaction.create(OFile);
            transaction.createRelation(relation, observation, imageTag);
        }
        OFile.setContent(transaction, imageTag, `componentDefectChart_${component.getName()}_${graphResult}`, dataUrlToBinary(dataUrl), 'image/png');
    }
}

registerBrick('018aaf7c3b1d8c38597b', GenerateChartImages);
